import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Container, Row, Col, Form, Spinner, Alert } from 'react-bootstrap';
import apiService from '../services/api';
import DataQualityDrilldownModal from './DataQualityDrilldownModal';

const DataQuality = () => {
    const [selectedOrganization, setSelectedOrganization] = useState('all');
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(true);
    const [organizationError, setOrganizationError] = useState('');

    const [setupMetrics, setSetupMetrics] = useState({
        usersWithNoRoles: null,
        teamsWithNoMembers: null,
        ssoWronglyClassifiedStorageLocation: null,
        salesRepWronglyClassifiedStorageLocation: null,
        userWithBothSsoAndSalesRepRoles: null,
        fieldUserWithNoTeam: null,
        teamLeaderWithNoTeamAssigned: null,
    });
    const [loadingSetup, setLoadingSetup] = useState(false);
    const [setupError, setSetupError] = useState('');

    const [inventoryMetrics, setInventoryMetrics] = useState({
        frozenSsoStock: null,
        soldInStockFrozen: null,
        soldInStockSsoStock: null,
        soldInStockSalesRepStock: null,
        soldInStockTeamStock: null,
        soldInStockWarehouseStock: null,
    });
    const [loadingInventory, setLoadingInventory] = useState(false);
    const [inventoryError, setInventoryError] = useState('');

    const [stockMovementMetrics, setStockMovementMetrics] = useState({
        frozenAccepted: null,
        frozenRejected: null,
        teamStockInitiated: null,
        salesRepStockInitiated: null,
        ssoStockInitiated: null,
        warehouseInitiated: null,
    });
    const [loadingStockMovement, setLoadingStockMovement] = useState(false);
    const [stockMovementError, setStockMovementError] = useState('');

    const [drilldownModal, setDrilldownModal] = useState({ show: false, title: '', metricKey: null });

    const openDrilldown = (metricKey, title) => {
        setDrilldownModal({ show: true, title, metricKey });
    };

    const closeDrilldown = () => {
        setDrilldownModal({ show: false, title: '', metricKey: null });
    };

    const getMetricColor = (value) => {
        if (value === null) return 'secondary';
        if (Number(value) === 0) return 'success';
        return 'danger';
    };

    useEffect(() => {
        loadOrganizations();
    }, []);

    const loadSetupMetrics = useCallback(async (organizationId) => {
        setLoadingSetup(true);
        setSetupError('');
        try {
            const [usersRes, teamsRes, ssoRes, salesRepRes, bothRolesRes, fieldUserRes, teamLeaderRes] = await Promise.all([
                apiService.getSetupUsersWithNoRolesCount(organizationId),
                apiService.getSetupTeamsWithNoMembersCount(organizationId),
                apiService.getSetupSsoWronglyClassifiedStorageLocationCount(organizationId),
                apiService.getSetupSalesRepWronglyClassifiedStorageLocationCount(organizationId),
                apiService.getSetupUserWithBothSsoAndSalesRepRolesCount(organizationId),
                apiService.getSetupFieldUserWithNoTeamCount(organizationId),
                apiService.getSetupTeamLeaderWithNoTeamAssignedCount(organizationId),
            ]);
            setSetupMetrics({
                usersWithNoRoles: usersRes.success ? usersRes.data?.count : null,
                teamsWithNoMembers: teamsRes.success ? teamsRes.data?.count : null,
                ssoWronglyClassifiedStorageLocation: ssoRes.success ? ssoRes.data?.count : null,
                salesRepWronglyClassifiedStorageLocation: salesRepRes.success ? salesRepRes.data?.count : null,
                userWithBothSsoAndSalesRepRoles: bothRolesRes.success ? bothRolesRes.data?.count : null,
                fieldUserWithNoTeam: fieldUserRes.success ? fieldUserRes.data?.count : null,
                teamLeaderWithNoTeamAssigned: teamLeaderRes.success ? teamLeaderRes.data?.count : null,
            });
            if (!usersRes.success || !teamsRes.success || !ssoRes.success || !salesRepRes.success || !bothRolesRes.success || !fieldUserRes.success || !teamLeaderRes.success) {
                setSetupError('Some setup metrics could not be loaded.');
            }
        } catch (error) {
            console.error('Error loading setup metrics:', error);
            setSetupError('Failed to load setup metrics.');
        } finally {
            setLoadingSetup(false);
        }
    }, []);

    const loadInventoryMetrics = useCallback(async (organizationId) => {
        setLoadingInventory(true);
        setInventoryError('');
        try {
            const [frozenRes, soldFrozenRes, soldSsoRes, soldSalesRepRes, soldTeamRes, soldWarehouseRes] = await Promise.all([
                apiService.getInventoryFrozenSsoStockCount(organizationId),
                apiService.getInventorySoldInStockCount(organizationId, 'FROZEN'),
                apiService.getInventorySoldInStockCount(organizationId, 'SSO STOCK'),
                apiService.getInventorySoldInStockCount(organizationId, 'SALES REP STOCK'),
                apiService.getInventorySoldInStockCount(organizationId, 'TEAM STOCK'),
                apiService.getInventorySoldInStockCount(organizationId, 'WAREHOUSE STOCK'),
            ]);
            setInventoryMetrics({
                frozenSsoStock: frozenRes.success ? frozenRes.data?.count : null,
                soldInStockFrozen: soldFrozenRes.success ? soldFrozenRes.data?.count : null,
                soldInStockSsoStock: soldSsoRes.success ? soldSsoRes.data?.count : null,
                soldInStockSalesRepStock: soldSalesRepRes.success ? soldSalesRepRes.data?.count : null,
                soldInStockTeamStock: soldTeamRes.success ? soldTeamRes.data?.count : null,
                soldInStockWarehouseStock: soldWarehouseRes.success ? soldWarehouseRes.data?.count : null,
            });
            if (!frozenRes.success || !soldFrozenRes.success || !soldSsoRes.success || !soldSalesRepRes.success || !soldTeamRes.success || !soldWarehouseRes.success) {
                setInventoryError('Some inventory metrics could not be loaded.');
            }
        } catch (error) {
            console.error('Error loading inventory metrics:', error);
            setInventoryError('Failed to load inventory metrics.');
        } finally {
            setLoadingInventory(false);
        }
    }, []);

    useEffect(() => {
        loadSetupMetrics(selectedOrganization);
    }, [selectedOrganization, loadSetupMetrics]);

    useEffect(() => {
        loadInventoryMetrics(selectedOrganization);
    }, [selectedOrganization, loadInventoryMetrics]);

    const loadStockMovementMetrics = useCallback(async (organizationId) => {
        setLoadingStockMovement(true);
        setStockMovementError('');
        try {
            const [frozenAcceptedRes, frozenRejectedRes, teamInitiatedRes, salesRepInitiatedRes, ssoInitiatedRes, warehouseInitiatedRes] = await Promise.all([
                apiService.getStockMovementMisalignedCount(organizationId, 'FROZEN', 'accepted'),
                apiService.getStockMovementMisalignedCount(organizationId, 'FROZEN', 'rejected'),
                apiService.getStockMovementMisalignedCount(organizationId, 'TEAM STOCK', 'initiated'),
                apiService.getStockMovementMisalignedCount(organizationId, 'SALES REP STOCK', 'initiated'),
                apiService.getStockMovementMisalignedCount(organizationId, 'SSO STOCK', 'initiated'),
                apiService.getStockMovementMisalignedCount(organizationId, 'WAREHOUSE', 'initiated'),
            ]);
            setStockMovementMetrics({
                frozenAccepted: frozenAcceptedRes.success ? frozenAcceptedRes.data?.count : null,
                frozenRejected: frozenRejectedRes.success ? frozenRejectedRes.data?.count : null,
                teamStockInitiated: teamInitiatedRes.success ? teamInitiatedRes.data?.count : null,
                salesRepStockInitiated: salesRepInitiatedRes.success ? salesRepInitiatedRes.data?.count : null,
                ssoStockInitiated: ssoInitiatedRes.success ? ssoInitiatedRes.data?.count : null,
                warehouseInitiated: warehouseInitiatedRes.success ? warehouseInitiatedRes.data?.count : null,
            });
            if (!frozenAcceptedRes.success || !frozenRejectedRes.success || !teamInitiatedRes.success || !salesRepInitiatedRes.success || !ssoInitiatedRes.success || !warehouseInitiatedRes.success) {
                setStockMovementError('Some stock movement metrics could not be loaded.');
            }
        } catch (error) {
            console.error('Error loading stock movement metrics:', error);
            setStockMovementError('Failed to load stock movement metrics.');
        } finally {
            setLoadingStockMovement(false);
        }
    }, []);

    useEffect(() => {
        loadStockMovementMetrics(selectedOrganization);
    }, [selectedOrganization, loadStockMovementMetrics]);

    const loadOrganizations = async () => {
        setLoadingOrganizations(true);
        setOrganizationError('');

        try {
            const response = await apiService.getOrganizations(1, 1000, '');
            if (response.success) {
                const orgList = [
                    { id: 'all', name: 'All Organizations' },
                    ...(response.data || []).map(org => ({
                        id: org.id,
                        name: org.name
                    }))
                ];
                setOrganizations(orgList);
            } else {
                setOrganizationError(response.error || 'Failed to load organizations');
            }
        } catch (error) {
            console.error('Error loading organizations:', error);
            setOrganizationError('Failed to load organizations');
        } finally {
            setLoadingOrganizations(false);
        }
    };

    return (
        <>
            <Container fluid className="py-4">
                {organizationError && (
                    <Alert variant="danger" dismissible onClose={() => setOrganizationError('')}>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {organizationError}
                    </Alert>
                )}

                {/* Organization Filter */}
                <Row className="mb-4">
                    <Col md={5} lg={4}>
                        <Card className="h-100">
                            <Card.Body>
                                <Form.Group>
                                    <Form.Label><strong>Filter by Organization</strong></Form.Label>
                                    {loadingOrganizations ? (
                                        <div className="text-center py-2">
                                            <Spinner animation="border" size="sm" />
                                            <span className="ms-2">Loading organizations...</span>
                                        </div>
                                    ) : (
                                        <Form.Select
                                            value={selectedOrganization}
                                            onChange={(e) => setSelectedOrganization(e.target.value)}
                                        >
                                            {organizations.map(org => (
                                                <option key={org.id} value={org.id}>
                                                    {org.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={7} lg={8}>
                        <Card className="h-100">
                            <Card.Body className="d-flex align-items-center justify-content-between">
                                <div>
                                    <h4 className="mb-1"><i className="bi bi-clipboard-data me-2"></i>Data Quality</h4>
                                    <p className="text-muted mb-0">Monitor and address data quality issues across customer organizations.</p>
                                </div>
                                <Button
                                    variant="outline-primary"
                                    onClick={() => {
                                        loadSetupMetrics(selectedOrganization);
                                        loadInventoryMetrics(selectedOrganization);
                                        loadStockMovementMetrics(selectedOrganization);
                                    }}
                                    disabled={loadingSetup || loadingInventory || loadingStockMovement}
                                >
                                    {(loadingSetup || loadingInventory || loadingStockMovement) ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Refreshing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-clockwise me-2"></i>Refresh
                                        </>
                                    )}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Row className="mb-4 align-items-stretch">
                    {/* Customer Organization Setup */}
                    <Col lg={6} className="mb-4 mb-lg-0">
                        <Card className="h-100">
                            <Card.Header>
                                <h5 className="mb-0"><i className="bi bi-gear me-2"></i>Customer Organization Setup</h5>
                            </Card.Header>
                            <Card.Body>
                                {setupError && (
                                    <Alert variant="warning" dismissible onClose={() => setSetupError('')}>
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {setupError}
                                    </Alert>
                                )}
                                {loadingSetup ? (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" />
                                        <span className="ms-2">Loading setup metrics...</span>
                                    </div>
                                ) : (
                                    <Row>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(setupMetrics.usersWithNoRoles)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(setupMetrics.usersWithNoRoles)}`}>
                                                        {setupMetrics.usersWithNoRoles >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('usersWithNoRoles', 'Users with No Roles')}
                                                            >
                                                                {setupMetrics.usersWithNoRoles}
                                                            </span>
                                                        ) : (
                                                            setupMetrics.usersWithNoRoles !== null ? setupMetrics.usersWithNoRoles : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-person-x me-1"></i>Users with No Roles
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(setupMetrics.teamsWithNoMembers)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(setupMetrics.teamsWithNoMembers)}`}>
                                                        {setupMetrics.teamsWithNoMembers >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('teamsWithNoMembers', 'Teams with No Members')}
                                                            >
                                                                {setupMetrics.teamsWithNoMembers}
                                                            </span>
                                                        ) : (
                                                            setupMetrics.teamsWithNoMembers !== null ? setupMetrics.teamsWithNoMembers : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-people me-1"></i>Teams with No Members
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(setupMetrics.ssoWronglyClassifiedStorageLocation)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(setupMetrics.ssoWronglyClassifiedStorageLocation)}`}>
                                                        {setupMetrics.ssoWronglyClassifiedStorageLocation >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('ssoWronglyClassifiedStorageLocation', 'SSOs with Wrongly Classified Storage Location')}
                                                            >
                                                                {setupMetrics.ssoWronglyClassifiedStorageLocation}
                                                            </span>
                                                        ) : (
                                                            setupMetrics.ssoWronglyClassifiedStorageLocation !== null ? setupMetrics.ssoWronglyClassifiedStorageLocation : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-geo me-1"></i>SSOs with Wrongly Classified Storage Location
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(setupMetrics.salesRepWronglyClassifiedStorageLocation)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(setupMetrics.salesRepWronglyClassifiedStorageLocation)}`}>
                                                        {setupMetrics.salesRepWronglyClassifiedStorageLocation >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('salesRepWronglyClassifiedStorageLocation', 'Sales Reps with Wrongly Classified Storage Location')}
                                                            >
                                                                {setupMetrics.salesRepWronglyClassifiedStorageLocation}
                                                            </span>
                                                        ) : (
                                                            setupMetrics.salesRepWronglyClassifiedStorageLocation !== null ? setupMetrics.salesRepWronglyClassifiedStorageLocation : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-person-badge me-1"></i>Sales Reps with Wrongly Classified Storage Location
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(setupMetrics.userWithBothSsoAndSalesRepRoles)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(setupMetrics.userWithBothSsoAndSalesRepRoles)}`}>
                                                        {setupMetrics.userWithBothSsoAndSalesRepRoles >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('userWithBothSsoAndSalesRepRoles', 'Users with Both SSO and Sales Rep Roles')}
                                                            >
                                                                {setupMetrics.userWithBothSsoAndSalesRepRoles}
                                                            </span>
                                                        ) : (
                                                            setupMetrics.userWithBothSsoAndSalesRepRoles !== null ? setupMetrics.userWithBothSsoAndSalesRepRoles : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-person-fill-exclamation me-1"></i>Users with Both SSO and Sales Rep Roles
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(setupMetrics.fieldUserWithNoTeam)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(setupMetrics.fieldUserWithNoTeam)}`}>
                                                        {setupMetrics.fieldUserWithNoTeam >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('fieldUserWithNoTeam', 'Field Users with No Team')}
                                                            >
                                                                {setupMetrics.fieldUserWithNoTeam}
                                                            </span>
                                                        ) : (
                                                            setupMetrics.fieldUserWithNoTeam !== null ? setupMetrics.fieldUserWithNoTeam : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-person-dash me-1"></i>Field Users with No Team
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(setupMetrics.teamLeaderWithNoTeamAssigned)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(setupMetrics.teamLeaderWithNoTeamAssigned)}`}>
                                                        {setupMetrics.teamLeaderWithNoTeamAssigned >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('teamLeaderWithNoTeamAssigned', 'Team Leader with no Team Assigned')}
                                                            >
                                                                {setupMetrics.teamLeaderWithNoTeamAssigned}
                                                            </span>
                                                        ) : (
                                                            setupMetrics.teamLeaderWithNoTeamAssigned !== null ? setupMetrics.teamLeaderWithNoTeamAssigned : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-person-gear me-1"></i>Team Leader with no Team Assigned
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                    {/* Inventory Management */}
                    <Col lg={6}>
                        <Card className="h-100">
                            <Card.Header>
                                <h5 className="mb-0"><i className="bi bi-boxes me-2"></i>Inventory Management</h5>
                            </Card.Header>
                            <Card.Body>
                                {inventoryError && (
                                    <Alert variant="warning" dismissible onClose={() => setInventoryError('')}>
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {inventoryError}
                                    </Alert>
                                )}
                                {loadingInventory ? (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" />
                                        <span className="ms-2">Loading inventory metrics...</span>
                                    </div>
                                ) : (
                                    <Row>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(inventoryMetrics.frozenSsoStock)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(inventoryMetrics.frozenSsoStock)}`}>
                                                        {inventoryMetrics.frozenSsoStock >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('frozenSsoStock', 'Frozen SSO Stock')}
                                                            >
                                                                {inventoryMetrics.frozenSsoStock}
                                                            </span>
                                                        ) : (
                                                            inventoryMetrics.frozenSsoStock !== null ? inventoryMetrics.frozenSsoStock : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-snow me-1"></i>Frozen SSO Stock
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(inventoryMetrics.soldInStockFrozen)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(inventoryMetrics.soldInStockFrozen)}`}>
                                                        {inventoryMetrics.soldInStockFrozen >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('soldInStockFrozen', 'Sold-In-Stock: Frozen')}
                                                            >
                                                                {inventoryMetrics.soldInStockFrozen}
                                                            </span>
                                                        ) : (
                                                            inventoryMetrics.soldInStockFrozen !== null ? inventoryMetrics.soldInStockFrozen : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-bag-x me-1"></i>Sold-In-Stock: Frozen
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(inventoryMetrics.soldInStockSsoStock)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(inventoryMetrics.soldInStockSsoStock)}`}>
                                                        {inventoryMetrics.soldInStockSsoStock >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('soldInStockSsoStock', 'Sold-In-Stock: SSO Stock')}
                                                            >
                                                                {inventoryMetrics.soldInStockSsoStock}
                                                            </span>
                                                        ) : (
                                                            inventoryMetrics.soldInStockSsoStock !== null ? inventoryMetrics.soldInStockSsoStock : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-bag-x me-1"></i>Sold-In-Stock: SSO Stock
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(inventoryMetrics.soldInStockSalesRepStock)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(inventoryMetrics.soldInStockSalesRepStock)}`}>
                                                        {inventoryMetrics.soldInStockSalesRepStock >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('soldInStockSalesRepStock', 'Sold-In-Stock: Sales Rep Stock')}
                                                            >
                                                                {inventoryMetrics.soldInStockSalesRepStock}
                                                            </span>
                                                        ) : (
                                                            inventoryMetrics.soldInStockSalesRepStock !== null ? inventoryMetrics.soldInStockSalesRepStock : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-bag-x me-1"></i>Sold-In-Stock: Sales Rep Stock
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(inventoryMetrics.soldInStockTeamStock)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(inventoryMetrics.soldInStockTeamStock)}`}>
                                                        {inventoryMetrics.soldInStockTeamStock >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('soldInStockTeamStock', 'Sold-In-Stock: Team Stock')}
                                                            >
                                                                {inventoryMetrics.soldInStockTeamStock}
                                                            </span>
                                                        ) : (
                                                            inventoryMetrics.soldInStockTeamStock !== null ? inventoryMetrics.soldInStockTeamStock : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-bag-x me-1"></i>Sold-In-Stock: Team Stock
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(inventoryMetrics.soldInStockWarehouseStock)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(inventoryMetrics.soldInStockWarehouseStock)}`}>
                                                        {inventoryMetrics.soldInStockWarehouseStock >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('soldInStockWarehouseStock', 'Sold-In-Stock: Warehouse Stock')}
                                                            >
                                                                {inventoryMetrics.soldInStockWarehouseStock}
                                                            </span>
                                                        ) : (
                                                            inventoryMetrics.soldInStockWarehouseStock !== null ? inventoryMetrics.soldInStockWarehouseStock : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-bag-x me-1"></i>Sold-In-Stock: Warehouse Stock
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                <Row className="mb-4 align-items-stretch">
                    {/* Stock Movements */}
                    <Col lg={6} className="mb-4 mb-lg-0">
                        <Card className="h-100">
                            <Card.Header>
                                <h5 className="mb-0"><i className="bi bi-arrow-left-right me-2"></i>Stock Movements</h5>
                            </Card.Header>
                            <Card.Body>
                                {stockMovementError && (
                                    <Alert variant="warning" dismissible onClose={() => setStockMovementError('')}>
                                        <i className="bi bi-exclamation-triangle me-2"></i>
                                        {stockMovementError}
                                    </Alert>
                                )}
                                {loadingStockMovement ? (
                                    <div className="text-center py-3">
                                        <Spinner animation="border" size="sm" />
                                        <span className="ms-2">Loading stock movement metrics...</span>
                                    </div>
                                ) : (
                                    <Row>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(stockMovementMetrics.frozenAccepted)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(stockMovementMetrics.frozenAccepted)}`}>
                                                        {stockMovementMetrics.frozenAccepted >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('stockMovementFrozenAccepted', 'Frozen Stock with Accepted Order')}
                                                            >
                                                                {stockMovementMetrics.frozenAccepted}
                                                            </span>
                                                        ) : (
                                                            stockMovementMetrics.frozenAccepted !== null ? stockMovementMetrics.frozenAccepted : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-snow me-1"></i>Frozen Stock with Accepted Order
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(stockMovementMetrics.frozenRejected)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(stockMovementMetrics.frozenRejected)}`}>
                                                        {stockMovementMetrics.frozenRejected >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('stockMovementFrozenRejected', 'Frozen Stock with Rejected Order')}
                                                            >
                                                                {stockMovementMetrics.frozenRejected}
                                                            </span>
                                                        ) : (
                                                            stockMovementMetrics.frozenRejected !== null ? stockMovementMetrics.frozenRejected : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-snow2 me-1"></i>Frozen Stock with Rejected Order
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(stockMovementMetrics.teamStockInitiated)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(stockMovementMetrics.teamStockInitiated)}`}>
                                                        {stockMovementMetrics.teamStockInitiated >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('stockMovementTeamStockInitiated', 'Team Stock with Initiated Order')}
                                                            >
                                                                {stockMovementMetrics.teamStockInitiated}
                                                            </span>
                                                        ) : (
                                                            stockMovementMetrics.teamStockInitiated !== null ? stockMovementMetrics.teamStockInitiated : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-people me-1"></i>Team Stock with Initiated Order
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(stockMovementMetrics.salesRepStockInitiated)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(stockMovementMetrics.salesRepStockInitiated)}`}>
                                                        {stockMovementMetrics.salesRepStockInitiated >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('stockMovementSalesRepStockInitiated', 'Sales Rep Stock with Initiated Order')}
                                                            >
                                                                {stockMovementMetrics.salesRepStockInitiated}
                                                            </span>
                                                        ) : (
                                                            stockMovementMetrics.salesRepStockInitiated !== null ? stockMovementMetrics.salesRepStockInitiated : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-person-badge me-1"></i>Sales Rep Stock with Initiated Order
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(stockMovementMetrics.ssoStockInitiated)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(stockMovementMetrics.ssoStockInitiated)}`}>
                                                        {stockMovementMetrics.ssoStockInitiated >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('stockMovementSsoStockInitiated', 'SSO Stock with Initiated Order')}
                                                            >
                                                                {stockMovementMetrics.ssoStockInitiated}
                                                            </span>
                                                        ) : (
                                                            stockMovementMetrics.ssoStockInitiated !== null ? stockMovementMetrics.ssoStockInitiated : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-geo me-1"></i>SSO Stock with Initiated Order
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                        <Col sm={6} md={4} lg={3} className="mb-3">
                                            <Card className={`text-center border-${getMetricColor(stockMovementMetrics.warehouseInitiated)}`}>
                                                <Card.Body>
                                                    <div className={`fs-2 fw-bold text-${getMetricColor(stockMovementMetrics.warehouseInitiated)}`}>
                                                        {stockMovementMetrics.warehouseInitiated >= 1 ? (
                                                            <span
                                                                role="button"
                                                                style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                                                onClick={() => openDrilldown('stockMovementWarehouseInitiated', 'Warehouse Stock with Initiated Order')}
                                                            >
                                                                {stockMovementMetrics.warehouseInitiated}
                                                            </span>
                                                        ) : (
                                                            stockMovementMetrics.warehouseInitiated !== null ? stockMovementMetrics.warehouseInitiated : '—'
                                                        )}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        <i className="bi bi-building me-1"></i>Warehouse Stock with Initiated Order
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                    {/* Sales */}
                    <Col lg={6}>
                        <Card className="h-100">
                            <Card.Header>
                                <h5 className="mb-0"><i className="bi bi-cart-check me-2"></i>Sales</h5>
                            </Card.Header>
                            <Card.Body>
                                <p className="text-muted">Sales data quality metrics coming soon.
                                    Draft Areas:
                                    <ul>
                                        <li>Sales Reconciliation Discrepancies</li>
                                        <li>Hanging Sales Records</li>
                                        <li>Sales Timeline Accuracy</li>
                                        <li>Sales Data Completeness</li>
                                        <li>Sales Data Consistency</li>
                                        <li>Sales Data Validation Errors</li>
                                        <li>Sales Data Anomalies</li>
                                        <li>Sales Data Duplication</li>
                                        <li>Sales Data Timeliness</li>
                                        <li>Sales Data Integrity Issues</li>
                                    </ul>
                                </p>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <DataQualityDrilldownModal
                show={drilldownModal.show}
                onHide={closeDrilldown}
                title={drilldownModal.title}
                metricKey={drilldownModal.metricKey}
                organizationId={selectedOrganization}
            />
        </>
    );
};

export default DataQuality;
